// Amazon-style Mirror Zoom Effect for Shopify Product Images
// Add this script to your theme assets folder as 'mirror-zoom.js'

class MirrorZoom {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Only initialize on desktop devices
    if (window.innerWidth < 990) return;

    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;

    // Create zoom mirror container
    this.createZoomMirror();

    // Setup observers for dynamic content
    this.observeGallery(mediaGallery);
    
    // Initial setup for existing images
    this.attachZoomToImages(mediaGallery);
  }

  createZoomMirror() {
    // Check if mirror already exists
    if (document.getElementById('zoom-mirror')) return;

    const mirror = document.createElement('div');
    mirror.id = 'zoom-mirror';
    mirror.className = 'zoom-mirror';
    mirror.innerHTML = '<div class="zoom-mirror-image"></div>';
    document.body.appendChild(mirror);

    // Add styles
    this.addStyles();
  }

  addStyles() {
    if (document.getElementById('mirror-zoom-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'mirror-zoom-styles';
    styles.textContent = `
      .zoom-mirror {
        position: absolute;
        width: 400px;
        height: 400px;
        border: 1px solid #ddd;
        background: white;
        display: none;
        z-index: 9999;
        overflow: hidden;
        pointer-events: none;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        border-radius: 4px;
      }

      .zoom-mirror-image {
        position: absolute;
        background-repeat: no-repeat;
        background-size: cover;
        width: 100%;
        height: 100%;
        transform-origin: top left;
      }

      .zoom-lens {
        position: absolute;
        border: 2px solid rgba(0,0,0,0.3);
        background: rgba(255,255,255,0.3);
        cursor: crosshair;
        pointer-events: none;
        display: none;
        z-index: 100;
      }

      .zoom-active {
        cursor: crosshair !important;
      }

      @media screen and (max-width: 989px) {
        .zoom-mirror,
        .zoom-lens {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(styles);
  }

  attachZoomToImages(container) {
    const images = container.querySelectorAll('img[src*="cdn.shopify.com"]');
    
    images.forEach(img => {
      // Skip if already has zoom
      if (img.dataset.zoomEnabled) return;
      
      img.dataset.zoomEnabled = 'true';
      
      // Create lens for this image
      const lens = document.createElement('div');
      lens.className = 'zoom-lens';
      
      const wrapper = img.closest('li') || img.parentElement;
      if (!wrapper) return;
      
      wrapper.style.position = 'relative';
      wrapper.appendChild(lens);

      // Event listeners
      img.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, img, lens));
      img.addEventListener('mousemove', (e) => this.handleMouseMove(e, img, lens));
      img.addEventListener('mouseleave', (e) => this.handleMouseLeave(e, lens));
    });
  }

  handleMouseEnter(e, img, lens) {
    const mirror = document.getElementById('zoom-mirror');
    if (!mirror) return;

    // Get high-res image URL
    let imgSrc = img.src;
    // Replace size parameter with larger size for zoom
    imgSrc = imgSrc.replace(/_\d+x\d+\./g, '_1200x1200.');
    imgSrc = imgSrc.replace(/\.(jpg|jpeg|png|webp).*$/, '_1200x1200.$1');

    const mirrorImg = mirror.querySelector('.zoom-mirror-image');
    mirrorImg.style.backgroundImage = `url(${imgSrc})`;

    // Calculate zoom ratio
    const imgRect = img.getBoundingClientRect();
    this.zoomRatio = 2.5; // Zoom level
    
    // Set lens size
    const lensWidth = imgRect.width / this.zoomRatio;
    const lensHeight = imgRect.height / this.zoomRatio;
    lens.style.width = lensWidth + 'px';
    lens.style.height = lensHeight + 'px';

    // Show elements
    lens.style.display = 'block';
    mirror.style.display = 'block';
    img.classList.add('zoom-active');

    // Store data for mousemove
    this.currentImg = img;
    this.currentLens = lens;
  }

  handleMouseMove(e, img, lens) {
    const mirror = document.getElementById('zoom-mirror');
    if (!mirror) return;

    const imgRect = img.getBoundingClientRect();
    
    // Calculate lens position
    let x = e.clientX - imgRect.left;
    let y = e.clientY - imgRect.top;
    
    const lensWidth = parseFloat(lens.style.width);
    const lensHeight = parseFloat(lens.style.height);

    // Keep lens within image bounds
    x = Math.max(lensWidth / 2, Math.min(x, imgRect.width - lensWidth / 2));
    y = Math.max(lensHeight / 2, Math.min(y, imgRect.height - lensHeight / 2));

    // Position lens
    lens.style.left = (x - lensWidth / 2) + 'px';
    lens.style.top = (y - lensHeight / 2) + 'px';

    // Position mirror - to the right of the image
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    
    mirror.style.left = (imgRect.right + scrollX + 20) + 'px';
    mirror.style.top = (imgRect.top + scrollY) + 'px';

    // Calculate background position for zoomed image
    const mirrorImg = mirror.querySelector('.zoom-mirror-image');
    const bgPosX = -(x * this.zoomRatio - 200);
    const bgPosY = -(y * this.zoomRatio - 200);
    
    mirrorImg.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
    mirrorImg.style.backgroundSize = `${imgRect.width * this.zoomRatio}px ${imgRect.height * this.zoomRatio}px`;
  }

  handleMouseLeave(e, lens) {
    const mirror = document.getElementById('zoom-mirror');
    if (!mirror) return;

    lens.style.display = 'none';
    mirror.style.display = 'none';
    
    if (this.currentImg) {
      this.currentImg.classList.remove('zoom-active');
    }
  }

  observeGallery(gallery) {
    // Watch for new images added dynamically
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.attachZoomToImages(gallery);
        }
      });
    });

    observer.observe(gallery, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when script loads
new MirrorZoom();